import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import Tooltip from './Tooltip';
import { AuthContext } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import { operateLights, testShellySwitch, purchaseCredits, grantAccess } from '../api';
import LightCreditsModal from './LightCreditsModal';
import PaymentModal from './PaymentModal';
import ErrorModal from './ErrorModal';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameById, getCourtDisplayNameFromCourt } from '../utils/courtDisplay';
import { buildCreditReference } from '../utils/paymentReference';

const parseOptionsList = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Error parsing booking options:', error);
    return [];
  }
};

/** Same bookingId + date + time slot (multi-court day group). */
const isSameDaySlot = (a, b) =>
  a.bookingDate === b.bookingDate &&
  a.startTime === b.startTime &&
  a.endTime === b.endTime;

const sortCourts = (courtA, courtB) => {
  const na = parseInt(String(courtA).split('-')[1], 10);
  const nb = parseInt(String(courtB).split('-')[1], 10);
  return (Number.isFinite(na) ? na : 0) - (Number.isFinite(nb) ? nb : 0);
};

/** Minimal parse for club `accessControl` from API (JSON string or object). */
const parseClubAccessControl = (raw) => {
  if (raw == null || raw === '') return null;
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const type = obj.type;
  if (type !== 'access_code' && type !== 'shelly') return null;
  const minutesBeforeStart = Number(obj.minutesBeforeStart);
  const code =
    type === 'access_code' ? String(obj.code ?? '').trim() : '';
  if (type === 'shelly') {
    const deviceId = String(obj.deviceId ?? '').trim();
    const digits = String(obj.channel ?? '').replace(/\D/g, '');
    const channel = digits === '' ? '0' : String(Number(digits));
    return {
      type,
      minutesBeforeStart: Number.isFinite(minutesBeforeStart) ? minutesBeforeStart : 0,
      code: '',
      deviceId,
      channel
    };
  }
  return {
    type,
    minutesBeforeStart: Number.isFinite(minutesBeforeStart) ? minutesBeforeStart : 0,
    code
  };
};

/** Single generic copy for grantAccess failures (details go to console for support). */
const GRANT_ACCESS_USER_MESSAGE =
  'An error occurred. Please contact the club to gain access.';

/** Matches Edit / Join primary actions in this modal */
const accessControlPrimaryButtonClass =
  'rounded-lg px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2';

const BookingDetailsModal = ({
  isOpen,
  onClose,
  selectedBooking,
  isSmallScreen,
  onDeleteBooking,
  onEditBooking,
  uniqueSportsCount,
  onJoinBooking,
  onShareBooking,
  updateBookingStatus,
  setPaymentDetails,
  setShowPaymentModal,
  setSelectedBooking,
  handleError,
  recurringGroups,
  viewBookings = []
}) => {
  const { user, loginUser } = useContext(AuthContext);
  const { getCategoryStyle, courtData, bookingOptions, clubDetails } = useClub();
  const [timeRemaining, setTimeRemaining] = useState('');
  const [lightsStatus, setLightsStatus] = useState({ type: '', message: '' });
  const [isOperatingLights, setIsOperatingLights] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [lightsActive, setLightsActive] = useState(false);
  const [lightsAction, setLightsAction] = useState(null);
  const [switchStatuses, setSwitchStatuses] = useState([]);
  const [switchStatusError, setSwitchStatusError] = useState('');
  const [isSwitchStatusLoading, setIsSwitchStatusLoading] = useState(false);
  const [showLightCreditsModal, setShowLightCreditsModal] = useState(false);
  const [showCreditPaymentModal, setShowCreditPaymentModal] = useState(false);
  const [creditPaymentDetails, setCreditPaymentDetails] = useState(null);
  const [showCreditErrorModal, setShowCreditErrorModal] = useState(false);
  const [creditErrorMessage, setCreditErrorMessage] = useState('');
  const creditPaymentDetailsRef = useRef(null);
  const switchStatusLoadInFlightRef = useRef(false);
  /** Set after user clicks “Show Access Code”; `null` until resolved (local or API). Reset when modal/booking changes. */
  const [revealedAccessCode, setRevealedAccessCode] = useState(null);
  const [accessCodeLoading, setAccessCodeLoading] = useState(false);
  const [openGateLoading, setOpenGateLoading] = useState(false);
  const [gateCooldownActive, setGateCooldownActive] = useState(false);
  /** Shown above the modal footer when grantAccess fails (access code or open gate), including axios timeout. */
  const [grantAccessInlineError, setGrantAccessInlineError] = useState('');

  useEffect(() => {
    if (!isOpen || !selectedBooking) return;

    const calculateTimeRemaining = () => {
      let startTime;
      
      // Parse public data for this calculation
      const publicData = selectedBooking.public;
      const publicInfo = publicData ? (() => {
        try {
          return typeof publicData === 'string' ? JSON.parse(publicData) : publicData;
        } catch {
          return null;
        }
      })() : null;
      
      // Check if this is a public booking where user is in pending
      if (publicData && publicInfo?.pending && publicInfo.pending[user.uuid]) {
        // Use the timestamp from public.pending for this user
        startTime = new Date(publicInfo.pending[user.uuid]);
      } else if (selectedBooking.createdAt) {
        // Use the booking creation time for regular bookings
        startTime = new Date(selectedBooking.createdAt);
      } else {
        return;
      }
      
      const deadline = new Date(startTime.getTime() + 20 * 60 * 1000); // 20 minutes from start time
      const now = new Date();
      const diff = deadline - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        //onClose();
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const timer = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(timer);
  }, [isOpen, selectedBooking, user]);

  useEffect(() => {
    if (!isOpen || !selectedBooking) {
      setSecondsRemaining(0);
      return;
    }

    const calculateSeconds = () => {
      const endDate = new Date(`${selectedBooking.bookingDate}T${selectedBooking.endTime}:00`);
      const diff = Math.floor((endDate.getTime() - Date.now()) / 1000);
      setSecondsRemaining(diff > 0 ? diff : 0);
    };

    calculateSeconds();
    const timer = setInterval(calculateSeconds, 1000);
    return () => clearInterval(timer);
  }, [isOpen, selectedBooking]);

  useEffect(() => {
    if (!isOpen) {
      setLightsStatus({ type: '', message: '' });
      setIsOperatingLights(false);
      setLightsActive(false);
      setLightsAction(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setLightsActive(false);
    setLightsStatus({ type: '', message: '' });
    setLightsAction(null);
  }, [selectedBooking]);

  useEffect(() => {
    if (switchStatuses.length > 0) {
      setLightsActive(switchStatuses.some(status => status.output));
    }
  }, [switchStatuses]);

  // Determine edit permissions based on joiners - must be before early return
  const canEditBooking = useMemo(() => {
    if (!selectedBooking || selectedBooking.createdBy !== user.uuid) {
      return false; // Only creator can edit
    }

    // Parse public data for this calculation
    const publicData = selectedBooking.public;
    const publicInfo = publicData ? (() => {
      try {
        return typeof publicData === 'string' ? JSON.parse(publicData) : publicData;
      } catch {
        return null;
      }
    })() : null;

    // Calculate total joined users (pending + confirmed)
    const totalJoinedUsers = publicInfo ? 
      (Object.keys(publicInfo.pending || {}).length + Object.keys(publicInfo.confirmed || {}).length) : 0;

    // If booking is not public, allow full edit
    if (!publicData) {
      return { canEdit: true, editMode: 'full' };
    }

    // If booking is public, check for joiners
    const totalJoiners = totalJoinedUsers;
    
    if (totalJoiners === 0) {
      // No joiners - check if this is part of a recurring group
      if (selectedBooking.bookingId && recurringGroups) {
        const groupedBookings = recurringGroups[selectedBooking.bookingId] || [];
        // Check if ALL bookings in the group have no joiners
        const allBookingsHaveNoJoiners = groupedBookings.every(booking => {
          if (!booking.public) return true; // Non-public bookings are fine
          try {
            const bookingPublicInfo = typeof booking.public === 'string' ? JSON.parse(booking.public) : booking.public;
            const bookingJoiners = (Object.keys(bookingPublicInfo.pending || {}).length + Object.keys(bookingPublicInfo.confirmed || {}).length);
            return bookingJoiners === 0;
          } catch {
            return true; // If parsing fails, assume no joiners
          }
        });
        
        if (allBookingsHaveNoJoiners) {
          return { canEdit: true, editMode: 'full' };
        } else {
          return { canEdit: true, editMode: 'individual' };
        }
      } else {
        // Single booking with no joiners
        return { canEdit: true, editMode: 'full' };
      }
    } else {
      // Has joiners - check if this is a multi-court booking for the same day
      if (selectedBooking.bookingId && recurringGroups) {
        const groupedBookings = recurringGroups[selectedBooking.bookingId] || [];
        
        // Check if this is a multi-court booking for the same day (same date, same time, multiple courts)
        const sameDayBookings = groupedBookings.filter(booking => 
          booking.bookingDate === selectedBooking.bookingDate && 
          booking.startTime === selectedBooking.startTime &&
          booking.endTime === selectedBooking.endTime
        );
        
        const isMultiCourtBooking = sameDayBookings.length > 1;
        
        if (isMultiCourtBooking) {
          // For multi-court bookings with joiners (recurring or not), allow editing all courts on the same day
          // This will be handled as same-day editing, not full series editing
          return { canEdit: true, editMode: 'full' };
        } else {
          // Single court booking with joiners - only allow individual edit
          return { canEdit: true, editMode: 'individual' };
        }
      } else {
        // Single booking with joiners - only allow individual edit
        return { canEdit: true, editMode: 'individual' };
      }
    }
  }, [selectedBooking, user, recurringGroups]);

  // Multi-court rows for the same day slot: creator's group bookings + calendar/court view.
  const sameDayGroupBookings = useMemo(() => {
    if (!selectedBooking) return [];
    if (!selectedBooking.bookingId) return [selectedBooking];

    const fromUser = (recurringGroups?.[selectedBooking.bookingId] || []).filter((b) =>
      isSameDaySlot(b, selectedBooking)
    );
    const fromView = (viewBookings || []).filter(
      (b) => b.bookingId === selectedBooking.bookingId && isSameDaySlot(b, selectedBooking)
    );

    const byCourt = new Map();
    for (const b of [...fromUser, ...fromView, selectedBooking]) {
      if (b?.court) byCourt.set(b.court, b);
    }
    return [...byCourt.keys()].sort(sortCourts).map((court) => byCourt.get(court));
  }, [selectedBooking, recurringGroups, viewBookings]);

  const multiCourtHeaderLabel = useMemo(() => {
    if (sameDayGroupBookings.length <= 1) return null;

    const courts = sameDayGroupBookings.map((b) => b.court);
    const courtType = courts[0].split('-')[0];
    const sportName = formatSportDisplayName(courtType);
    const displayName = uniqueSportsCount === 1 ? 'Court' : sportName;
    const fallbackPrefix = uniqueSportsCount === 1 ? 'Court' : sportName;
    const courtAliases = courts.map((courtId) => getCourtDisplayNameById(courtId, courtData, '').trim());
    const hasAnyAlias = courtAliases.some(Boolean);

    if (!hasAnyAlias) {
      const numbers = courts.map((c) => c.split('-')[1]).join(', ');
      return `${displayName} ${numbers}`;
    }
    const labels = courts.map(
      (courtId, idx) => courtAliases[idx] || getCourtDisplayNameById(courtId, courtData, fallbackPrefix)
    );
    return labels.join(', ');
  }, [sameDayGroupBookings, courtData, uniqueSportsCount]);

  const courtDetails = useMemo(() => {
    if (!selectedBooking) return null;
    if (selectedBooking.courtDetails) return selectedBooking.courtDetails;
    if (!courtData || courtData.length === 0) return null;
    return courtData.find((courtItem) => courtItem.court === selectedBooking.court) || null;
  }, [selectedBooking, courtData]);
  const sportKey = selectedBooking?.court?.split('-')[0] || null;
  const sportPermissions = sportKey ? (user?.permissions?.[sportKey] || {}) : {};
  const canOperateLights = !!sportPermissions.canOperateLights;
  const lightsFeePerMinute = Number(sportPermissions.lightsFeePerMinute || 0);
  const cancellationCutoffHours = Math.max(0, Number(sportPermissions.cancellationCutoffHours || 0));
  const userCredits = Number(user?.credits ?? 0) || 0;
  const getCurrentClubTime = () => {
    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    return new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
  };
  const bookingHasStarted = useMemo(() => {
    if (!selectedBooking) return false;
    const startDate = new Date(`${selectedBooking.bookingDate}T${selectedBooking.startTime}:00`);
    return startDate <= getCurrentClubTime();
  }, [selectedBooking]);
  const bookingHasEnded = useMemo(() => {
    if (!selectedBooking) return false;
    const endDate = new Date(`${selectedBooking.bookingDate}T${selectedBooking.endTime}:00`);
    return endDate <= getCurrentClubTime();
  }, [selectedBooking]);
  const isWithinCancellationWindow = useMemo(() => {
    if (!selectedBooking) return false;
    const startDate = new Date(`${selectedBooking.bookingDate}T${selectedBooking.startTime}:00`);
    const cutoffTime = new Date(startDate.getTime() - cancellationCutoffHours * 60 * 60 * 1000);
    return getCurrentClubTime() >= cutoffTime;
  }, [selectedBooking, cancellationCutoffHours]);
  const isBookingOwner = selectedBooking?.createdBy === user.uuid;
  // Unpaid (pending) bookings must not grant access or light control until payment is received,
  // otherwise a user could book just before the slot, skip payment, and still get in / use lights.
  const isBookingPending = selectedBooking?.status === 'pending';
  const parsedClubAccessControl = useMemo(
    () => parseClubAccessControl(clubDetails?.accessControl),
    [clubDetails?.accessControl]
  );
  const showAccessControlAction = useMemo(() => {
    if (!selectedBooking || !isBookingOwner || isBookingPending || bookingHasEnded || !parsedClubAccessControl) return false;
    const acM = Math.max(0, parsedClubAccessControl.minutesBeforeStart);
    const cutoffM = Math.max(0, cancellationCutoffHours * 60);
    const clubNow = getCurrentClubTime();
    const startDate = new Date(`${selectedBooking.bookingDate}T${selectedBooking.startTime}:00`);
    const minutesToStart = (startDate.getTime() - clubNow.getTime()) / (60 * 1000);
    return minutesToStart < acM && minutesToStart < cutoffM;
  }, [
    selectedBooking,
    isBookingOwner,
    isBookingPending,
    bookingHasEnded,
    parsedClubAccessControl,
    cancellationCutoffHours,
    secondsRemaining
  ]);
  useEffect(() => {
    setRevealedAccessCode(null);
    setAccessCodeLoading(false);
    setOpenGateLoading(false);
    setGateCooldownActive(false);
    setGrantAccessInlineError('');
  }, [isOpen, selectedBooking]);

  const hasSwitches = Boolean(courtDetails?.switches && courtDetails.switches.length > 0);
  const hasFloodlights = Boolean(courtDetails?.floodlights);
  const publicData = selectedBooking?.public;
  const minutesRemaining = useMemo(() => {
    if (!selectedBooking) return 0;
    return Math.max(1, Math.ceil(secondsRemaining / 60));
  }, [selectedBooking, secondsRemaining]);
  const canLoadSwitchStatuses = useMemo(() => {
    return (
      !!selectedBooking &&
      isBookingOwner &&
      !isBookingPending &&
      canOperateLights &&
      hasFloodlights &&
      hasSwitches &&
      bookingHasStarted &&
      !bookingHasEnded
    );
  }, [selectedBooking, isBookingOwner, isBookingPending, canOperateLights, hasFloodlights, hasSwitches, bookingHasStarted, bookingHasEnded]);
  const shouldShowLightControls = useMemo(() => canLoadSwitchStatuses && secondsRemaining > 0, [
    canLoadSwitchStatuses,
    secondsRemaining
  ]);
  const estimatedCost = lightsFeePerMinute * minutesRemaining;
  const normalizeSwitchResponse = useCallback((response, retrievedAt = Date.now()) => {
    if (!response) return [];
    if (Array.isArray(response.devices)) {
      return response.devices.map(status => ({
        ...status,
        retrievedAt,
        offAt: status.offAt ? new Date(status.offAt) : null
      }));
    }
    if (response.deviceId) {
      const { deviceId, exists, online, output, timerDuration, offAt, error } = response;
      return [{
        deviceId,
        exists: exists ?? false,
        online: online ?? false,
        output: output ?? false,
        timerDuration: timerDuration ?? null,
        offAt: offAt ? new Date(offAt) : null,
        error: error || null,
        retrievedAt
      }];
    }
    return [];
  }, []);

  const loadSwitchStatuses = useCallback(async ({ force = false } = {}) => {
    if (!shouldShowLightControls || !selectedBooking) {
      setSwitchStatuses([]);
      setSwitchStatusError('');
      return;
    }

    if (switchStatusLoadInFlightRef.current && !force) {
      return;
    }
    switchStatusLoadInFlightRef.current = true;

    setIsSwitchStatusLoading(true);
    setSwitchStatusError('');
    try {
      const response = await testShellySwitch({ court: selectedBooking.court });
      const devices = normalizeSwitchResponse(response, Date.now());
      await new Promise((resolve) => setTimeout(resolve, 800));
      setSwitchStatuses(devices);
    } catch (error) {
      setSwitchStatuses([]);
      setSwitchStatusError(error?.message || 'Unable to retrieve switch status.');
    } finally {
      switchStatusLoadInFlightRef.current = false;
      setIsSwitchStatusLoading(false);
    }
  }, [
    normalizeSwitchResponse,
    selectedBooking,
    shouldShowLightControls
  ]);

  useEffect(() => {
    if (isOpen && shouldShowLightControls) {
      loadSwitchStatuses();
    } else if (!isOpen) {
      setSwitchStatuses([]);
      setSwitchStatusError('');
    }
  }, [
    isOpen,
    shouldShowLightControls,
    selectedBooking?.bookingDate,
    selectedBooking?.court,
    selectedBooking?.startTime,
    loadSwitchStatuses
  ]);

  const formatDeviceStatusMessage = useCallback((status) => {
    if (!status) return '';
    if (status.error) {
      return status.error;
    }
    if (!status.exists) {
      return 'Not registered';
    }
    if (!status.online) {
      return 'Offline';
    }
    if (!status.output) {
      return 'Online • Off';
    }
    if (status.offAt) {
      const offTime = status.offAt instanceof Date ? status.offAt : new Date(status.offAt);
      const timeLabel = offTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `On • auto-off at ${timeLabel}`;
    }
    if (status.timerDuration !== null && status.timerDuration !== undefined) {
      const baseTime = status.retrievedAt ? new Date(status.retrievedAt) : new Date();
      const offTime = new Date(baseTime.getTime() + status.timerDuration * 1000);
      const timeLabel = offTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `On • auto-off at ${timeLabel}`;
    }
    return 'On';
  }, []);

  const anySwitchOn = useMemo(() => switchStatuses.some(status => status.output), [switchStatuses]);
  const anySwitchOffline = useMemo(
    () => switchStatuses.some(status => status.error || !status.exists || !status.online),
    [switchStatuses]
  );
  const hasSufficientCredits = useMemo(() => {
    const required = minutesRemaining * lightsFeePerMinute;
    return userCredits >= required;
  }, [minutesRemaining, lightsFeePerMinute, userCredits]);

  // Calculate credit shortfall for top-up suggestion
  const creditShortfall = useMemo(() => {
    if (hasSufficientCredits) return 0;
    const required = minutesRemaining * lightsFeePerMinute;
    return Math.max(0, required - userCredits);
  }, [hasSufficientCredits, minutesRemaining, lightsFeePerMinute, userCredits]);
  const disableTurnOn = isSwitchStatusLoading || isOperatingLights || secondsRemaining <= 10 || anySwitchOn || anySwitchOffline || !hasSufficientCredits;
  const disableTurnOff = isSwitchStatusLoading || isOperatingLights || secondsRemaining <= 10 || !anySwitchOn;

  // The gate switch status is intentionally not pre-checked: the Open Gate button stays enabled and
  // any problem surfaces as an inline error after the user clicks (which then disables the button).
  const disableOpenGateButton =
    openGateLoading ||
    gateCooldownActive ||
    secondsRemaining <= 10 ||
    Boolean(grantAccessInlineError);

  const handleLightsAction = async (action) => {
    if (!selectedBooking) return;
    if (secondsRemaining <= 0) {
      setLightsStatus({
        type: 'error',
        message: 'This booking has ended. Floodlights cannot be controlled.'
      });
      return;
    }

    try {
      setIsOperatingLights(true);
      setLightsAction(action);
      setLightsStatus({ type: '', message: '' });
      const response = await operateLights({
        court: selectedBooking.court,
        bookingDate: selectedBooking.bookingDate,
        endTime: selectedBooking.endTime,
        courtStartTime: selectedBooking['court#startTime'] || `${selectedBooking.court}#${selectedBooking.startTime}`,
        action
      });

      if (response?.token) {
        loginUser(response.token);
      }

      const amountNote = response?.creditDelta
        ? `${formatCurrency(response.creditDelta)} ${action === 'on' ? 'deducted' : 'refunded'}.`
        : '';
      const creditsNote = typeof response?.credits === 'number'
        ? ` Available credits: ${formatCurrency(response.credits)}.`
        : '';

      setLightsStatus({
        type: 'success',
        message: ''
      });
      if (Array.isArray(response?.switchStatuses)) {
        const timestampedStatuses = response.switchStatuses.map(status => ({
          ...status,
          retrievedAt: Date.now(),
          offAt: status.offAt ? new Date(status.offAt) : null
        }));
        setSwitchStatuses(timestampedStatuses);
        setSwitchStatusError('');
        setLightsActive(timestampedStatuses.some(status => status.output));
      } else if (canLoadSwitchStatuses) {
        loadSwitchStatuses({ force: true });
      }
    } catch (error) {
      setLightsStatus({
        type: 'error',
        message: error?.message || `Unable to turn ${action === 'on' ? 'on' : 'off'} lights. Please try again.`
      });
    } finally {
      setIsOperatingLights(false);
      setLightsAction(null);
    }
  };

  const formatRecurringRule = (rule) => {
    if (!rule) return null;

    const { bookingDate, endTime } = selectedBooking;

    try {
      // Handle both old DynamoDB format and new transformed format
      const ruleString = rule.S || rule;
      const parsedRule = typeof ruleString === 'string' ? JSON.parse(ruleString) : ruleString;
      const frequency = parsedRule.frequency;
      
      if (!frequency) return null;
      return `${frequency.charAt(0).toUpperCase()}${frequency.slice(1)}`;
    } catch (error) {
      console.error('Error parsing recurring rule:', error);
      return null;
    }
  };

  // Parse public data once for reuse
  const publicInfo = publicData ? (() => {
    try {
      return typeof publicData === 'string' ? JSON.parse(publicData) : publicData;
    } catch {
      return null;
    }
  })() : null;

  // Calculate total joined users (pending + confirmed)
  const totalJoinedUsers = publicInfo ? 
    (Object.keys(publicInfo.pending || {}).length + Object.keys(publicInfo.confirmed || {}).length) : 0;
  
  // Get max spaces
  const maxSpaces = publicInfo?.max || 0;

  // Check if user can join this public booking
  const canJoin = publicData && user.userType && publicInfo && 
    (() => {
      // Check if user is already pending or confirmed
      if (publicInfo.pending && publicInfo.pending[user.uuid]) {
        return false; // User is already pending
      }
      if (publicInfo.confirmed && publicInfo.confirmed[user.uuid] !== undefined) {
        return false; // User is already confirmed
      }
      
      // Check if booking is full
      if (totalJoinedUsers >= maxSpaces) {
        return false; // Booking is full
      }
      
      // Check if membership has expired - if so, use userTypeId 0 (Newly Registered User)
      const effectiveUserTypeId = user.expiryDate && new Date(user.expiryDate) < new Date() 
        ? '0' 
        : String(user.userTypeId);
      
      return publicInfo.fee && publicInfo.fee.hasOwnProperty(effectiveUserTypeId);
    })();

  // Check if user has already joined this public booking
  const hasJoined = publicData && publicInfo && 
    ((publicInfo.pending && publicInfo.pending[user.uuid]) || 
     (publicInfo.confirmed && publicInfo.confirmed[user.uuid] !== undefined));

  // Check if user is specifically in pending (for showing Pay button)
  const isUserPending = publicData && publicInfo && 
    publicInfo.pending && publicInfo.pending[user.uuid];

  // Same cancellation cutoff as owner delete: confirmed joiners cannot leave once inside the window;
  // pending (unpaid) joiners can still leave to release their slot.
  const canLeavePublicBooking =
    hasJoined &&
    !bookingHasEnded &&
    (isUserPending || !isWithinCancellationWindow);

  // Calculate fee for display purposes
  const getEffectiveFee = () => {
    if (!publicData || !publicInfo) return 0;
    const effectiveUserTypeId = user.expiryDate && new Date(user.expiryDate) < new Date() 
      ? '0' 
      : String(user.userTypeId);
    return publicInfo.fee && publicInfo.fee[effectiveUserTypeId] ? publicInfo.fee[effectiveUserTypeId] : 0;
  };

  const effectiveFee = getEffectiveFee();

  const handleJoinBooking = async () => {
    if (!selectedBooking || !user) return;

    try {
      // Calculate fee based on userTypeId (considering expired memberships)
      const effectiveUserTypeId = user.expiryDate && new Date(user.expiryDate) < new Date() 
        ? '0' 
        : String(user.userTypeId);
      
      const fee = publicInfo.fee && publicInfo.fee[effectiveUserTypeId] ? publicInfo.fee[effectiveUserTypeId] : 0;
      
        const courtStartTime = `${selectedBooking.court}#${selectedBooking.startTime}`;

        if (fee > 0) {
          onJoinBooking({
            bookingId: selectedBooking.bookingId,
            bookingDate: selectedBooking.bookingDate,
            courtStartTime: courtStartTime,
            userUuid: user.uuid,
            fee: fee
          });
        } else {
          onJoinBooking({
            bookingId: selectedBooking.bookingId,
            bookingDate: selectedBooking.bookingDate,
            courtStartTime: courtStartTime,
            userUuid: user.uuid,
            fee: 0
          });
        }
    } catch (error) {
      console.error('Error joining booking:', error);
    }
  };

  const handleLeaveBooking = () => {
    if (!selectedBooking || !user) return;
    if (!canLeavePublicBooking) return;
    // Use the same delete function as the card delete - it will handle joined bookings correctly
    onDeleteBooking(selectedBooking);
  };

  const handlePayBooking = async () => {
    if (!selectedBooking || !user) return;

    try {
      // First, update the timestamp to reset the 10-minute timer
      const courtStartTime = `${selectedBooking.court}#${selectedBooking.startTime}`;
      
      const statusResponse = await updateBookingStatus({
        joinPayments: [{
          bookingDate: selectedBooking.bookingDate,
          courtStartTime: courtStartTime,
          userUuid: user.uuid,
          action: 'update_pending_timestamp'
        }]
      });

      const joinResult = statusResponse?.joinPaymentsResults?.find(result =>
        result.bookingDate === selectedBooking.bookingDate &&
        result.courtStartTime === courtStartTime &&
        result.userUuid === user.uuid
      );
      
      const pendingCreditsApplied = Number(publicInfo?.pendingCredits?.[user.uuid] ?? 0) || 0;
      const amountDue = Math.max(0, parseFloat((effectiveFee - pendingCreditsApplied).toFixed(2)));
      console.log('Join payment reference (retry):', joinResult?.paymentReference, `(${joinResult?.paymentReference?.length ?? 0} chars)`);

      setPaymentDetails({
        totalPaymentAmount: amountDue,
        fullFee: effectiveFee,
        reference: joinResult?.paymentReference,
        type: 'public_join',
        bookingId: selectedBooking.bookingId,
        bookingDate: selectedBooking.bookingDate,
        courtStartTime: courtStartTime,
        userUuid: user.uuid
      });
      
      // Close booking details modal and open payment modal
      setSelectedBooking(null);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error starting payment for pending booking:', error);
      handleError('Failed to start payment. Please try again.');
    }
  };

  const handleCreditPurchaseClick = (amount) => {
    if (!user) return;
    
    const reference = buildCreditReference(user.firstName, user.lastName);
    
    const paymentDetails = {
      totalPaymentAmount: amount,
      reference,
      userUuid: user.uuid,
      userDisplayName: `${user.firstName} ${user.lastName}`,
      type: 'credit_purchase',
      creditAmount: amount
    };
    
    creditPaymentDetailsRef.current = paymentDetails;
    setCreditPaymentDetails(paymentDetails);
    setShowCreditPaymentModal(true);
  };

  const handleCreditPaymentComplete = useCallback(async () => {
    const paymentDetails = creditPaymentDetailsRef.current;
    if (!paymentDetails) return;
    
    try {
      // Call the purchase credits API with reference and credit amount
      const response = await purchaseCredits(paymentDetails.reference, paymentDetails.creditAmount);
      
      // Update the token using loginUser to refresh user data
      if (response?.token) {
        loginUser(response.token);
      }
      
      // Close payment modal but keep LightCreditsModal open
      setShowCreditPaymentModal(false);
      creditPaymentDetailsRef.current = null;
      setCreditPaymentDetails(null);
      
      // The user data will be updated via loginUser, so the modal will show updated credits
    } catch (error) {
      console.error('Error purchasing credits:', error);
      // Close payment modal first to prevent multiple QR codes
      setShowCreditPaymentModal(false);
      // Show error modal to user with reference
      const reference = paymentDetails?.reference || 'N/A';
      setCreditErrorMessage(`Failed to purchase credits, please contact the club with Reference: ${reference}`);
      setShowCreditErrorModal(true);
      creditPaymentDetailsRef.current = null;
      setCreditPaymentDetails(null);
    }
  }, [loginUser]);

  const handleCreditPaymentModalClose = useCallback(() => {
    // Just close the payment modal, keep LightCreditsModal open
    setShowCreditPaymentModal(false);
    creditPaymentDetailsRef.current = null;
    setCreditPaymentDetails(null);
  }, []);

  useEffect(() => {
    if (!shouldShowLightControls) {
      setLightsStatus({ type: '', message: '' });
      setIsOperatingLights(false);
      setLightsAction(null);
    }
  }, [shouldShowLightControls]);

  const handleAccessCodeButtonClick = useCallback(async () => {
    if (revealedAccessCode !== null) return;
    if (!selectedBooking) return;
    try {
      setGrantAccessInlineError('');
      setAccessCodeLoading(true);
      const res = await grantAccess({
        bookingDate: selectedBooking.bookingDate,
        endTime: selectedBooking.endTime,
        courtStartTime:
          selectedBooking['court#startTime'] || `${selectedBooking.court}#${selectedBooking.startTime}`
      });
      if (res?.outcome === 'access_code' && res.code) {
        setRevealedAccessCode(res.code);
        setGrantAccessInlineError('');
      } else {
        console.error('[grantAccess] access code unexpected response', res);
        setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
      }
    } catch (err) {
      console.error('[grantAccess] access code request failed', err?.response?.data || err);
      setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
    } finally {
      setAccessCodeLoading(false);
    }
  }, [revealedAccessCode, selectedBooking]);

  const handleOpenGateClick = useCallback(async () => {
    if (!selectedBooking) return;
    try {
      setGrantAccessInlineError('');
      setOpenGateLoading(true);
      const res = await grantAccess({
        bookingDate: selectedBooking.bookingDate,
        endTime: selectedBooking.endTime,
        courtStartTime:
          selectedBooking['court#startTime'] || `${selectedBooking.court}#${selectedBooking.startTime}`
      });
      if (res?.outcome === 'shelly' && res.success) {
        setGrantAccessInlineError('');
      } else {
        console.error('[grantAccess] open gate unexpected response', res);
        setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
      }
    } catch (err) {
      console.error('[grantAccess] open gate request failed', err?.response?.data || err);
      setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
    } finally {
      setOpenGateLoading(false);
      setGateCooldownActive(true);
      window.setTimeout(() => setGateCooldownActive(false), 1000);
    }
  }, [selectedBooking]);

  if (!selectedBooking) return null;

  const {
    court,
    bookingDate,
    category,
    comments,
    createdByName,
    startTime,
    endTime,
    isRecurring,
    recurringRule
  } = selectedBooking;
  const bookingExtras = (() => {
    const ownExtras = parseOptionsList(selectedBooking?.options);
    if (ownExtras.length > 0) return ownExtras;

    // Extras live on one court row per day-group; resolve from any sibling in the group.
    const sibling = sameDayGroupBookings.find((b) => parseOptionsList(b?.options).length > 0);
    return sibling ? parseOptionsList(sibling.options) : [];
  })();
  const bookingOptionsNameMap = (bookingOptions || []).reduce((acc, option) => {
    if (option?.option) {
      acc[option.option] = option.name || option.option;
    }
    return acc;
  }, {});

  const formattedDate = new Date(bookingDate + 'T12:00:00Z').toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2
      }).format(numericValue);
    } catch {
      return `R${numericValue.toFixed(2)}`;
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        <Dialog.Panel className="relative w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <Dialog.Title className="text-xl font-semibold text-slate-800 mb-6">
            <span className="flex items-center">
              {(() => {
                const isUserPendingInPublic = publicData && (() => {
                  try {
                    const publicInfo = typeof publicData === 'string' ? JSON.parse(publicData) : publicData;
                    return publicInfo?.pending && publicInfo.pending[user.uuid];
                  } catch {
                    return false;
                  }
                })();
                return (selectedBooking.status === 'pending' || isUserPendingInPublic);
              })() && (
                <span className="text-red-600 mr-2 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5">
                    <circle cx="12" cy="12" r="10" fill="currentColor" />
                    <rect x="11" y="6" width="2" height="8" rx="1" fill="white" />
                    <circle cx="12" cy="17" r="1.2" fill="white" />
                  </svg>
                </span>
              )}
              <span>
                {(() => {
                  const sportKey = String(selectedBooking?.court || '').split('-')[0];
                  const sportName = sportKey ? formatSportDisplayName(sportKey) : '';
                  return sportName ? `${sportName} Booking Details` : 'Booking Details';
                })()}
              </span>
            </span>
          </Dialog.Title>
          {(() => {
            const isUserPendingInPublic = publicData && (() => {
              try {
                const publicInfo = typeof publicData === 'string' ? JSON.parse(publicData) : publicData;
                return publicInfo?.pending && publicInfo.pending[user.uuid];
              } catch {
                return false;
              }
            })();
            return (selectedBooking.status === 'pending' || isUserPendingInPublic);
          })() && (
            <div className="mb-4 text-red-600">
              <ul className="list-disc pl-5">
                <li>Payment not recieved</li>
                <li>Booking will be cancelled if not paid in <span className="font-bold">{timeRemaining}</span></li>
              </ul>
            </div>
          )}
          <div className="space-y-6">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex items-start justify-between mb-2">
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {(() => {
                      if (multiCourtHeaderLabel) {
                        return `${multiCourtHeaderLabel} - ${createdByName}`;
                      }
                      const aliasLabel = getCourtDisplayNameFromCourt(courtDetails, '').trim();
                      if (aliasLabel) return `${aliasLabel} - ${createdByName}`;
                      const courtType = selectedBooking.court.split('-')[0];
                      const sportName = formatSportDisplayName(courtType);
                      const displayName = uniqueSportsCount === 1 ? 'Court' : sportName;
                      const courtNumber = selectedBooking.court.split('-')[1];

                      return `${displayName} ${courtNumber} - ${createdByName}`;
                    })()}
                  </h3>
                  <p className="text-sm text-slate-800">{formattedDate}</p>
                  <p className="text-sm text-slate-800">
                    {formatTime(startTime)} - {formatTime(endTime)}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  {publicData && !bookingHasEnded && (
                    isSmallScreen ? (
                      <button
                        type="button"
                        onClick={() => onShareBooking?.(selectedBooking)}
                        className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="Share booking"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="18" cy="5" r="2"></circle>
                          <circle cx="6" cy="12" r="2"></circle>
                          <circle cx="18" cy="19" r="2"></circle>
                          <path d="M8 12l8-6"></path>
                          <path d="M8 12l8 6"></path>
                        </svg>
                      </button>
                    ) : (
                      <Tooltip content="Share Booking">
                        <button
                          type="button"
                          onClick={() => onShareBooking?.(selectedBooking)}
                          className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          aria-label="Share booking"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="2"></circle>
                            <circle cx="6" cy="12" r="2"></circle>
                            <circle cx="18" cy="19" r="2"></circle>
                            <path d="M8 12l8-6"></path>
                            <path d="M8 12l8 6"></path>
                          </svg>
                        </button>
                      </Tooltip>
                    )
                  )}
                  {selectedBooking.createdBy === user.uuid && !bookingHasEnded && !isWithinCancellationWindow && (
                    isSmallScreen ? (
                      <button
                        onClick={() => onDeleteBooking(selectedBooking)}
                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    ) : (
                      <Tooltip content="Delete Booking">
                        <button
                          onClick={() => onDeleteBooking(selectedBooking)}
                          className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </Tooltip>
                    )
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className={`inline-block px-2 py-1 rounded text-sm border ${getCategoryStyle({ 
                  category, 
                  court
                })}`}>
                  {category}
                </div>
                {isRecurring && recurringRule && (
                  <span className={`inline-block border ${getCategoryStyle({ 
                    category: 'Recurring', 
                    court 
                  })} px-2 py-1 rounded text-sm`}>
                    {formatRecurringRule(recurringRule)}
                  </span>
                )}
                {publicData && (
                  <span className={`inline-block border ${getCategoryStyle({ 
                    category: 'Available', 
                    court 
                  })} px-2 py-1 rounded text-sm`}>
                    {hasJoined ? 'Joined' : 'Public'} {totalJoinedUsers}/{maxSpaces}
                  </span>
                )}
              </div>

              {comments && (
                <div>
                  <p className="mt-2 text-sm text-slate-800">{comments}</p>
                </div>
              )}

              {bookingExtras.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold mb-2">Extras</p>
                  <div className="space-y-1">
                    {bookingExtras.map((option, index) => {
                      const name = bookingOptionsNameMap[option?.option] || option?.option || `Option ${index + 1}`;
                      const qtyValue = parseInt(option?.quantity ?? 1, 10);
                      const quantity = isNaN(qtyValue) || qtyValue < 1 ? 1 : qtyValue;
                      return (
                        <div key={`${option?.option || name}-${index}`} className="text-xs text-slate-800">
                          {name} x {quantity}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Show joiner names for public bookings */}
              {publicData && selectedBooking.joinerNames && (
                <div className="mt-3">
                  <div className="space-y-2">
                    {/* Confirmed joiners */}
                    {Object.keys(selectedBooking.joinerNames.confirmed || {}).length > 0 && (
                      <div>
                        <p className="text-xs font-medium mb-1">Confirmed ({Object.keys(selectedBooking.joinerNames.confirmed).length})</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(selectedBooking.joinerNames.confirmed).map(([uuid, joiner]) => (
                            <span key={uuid} className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded border border-green-200">
                              {joiner.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Pending joiners */}
                    {Object.keys(selectedBooking.joinerNames.pending || {}).length > 0 && (
                      <div>
                        <p className="text-xs font-medium mb-1">Pending Payment ({Object.keys(selectedBooking.joinerNames.pending).length})</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(selectedBooking.joinerNames.pending).map(([uuid, joiner]) => (
                            <span key={uuid} className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded border border-yellow-200">
                              {joiner.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {shouldShowLightControls && (
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
                {/* First row: Title on left, Credits and Top Up on right */}
                <div className="flex items-center justify-between text-sm text-blue-800">
                  <span className="font-semibold text-blue-800">Floodlight Controls</span>
                  <div className="flex items-center gap-2">
                    <span className={(() => {
                      // Only show red if lights are off, switches are online, button could be enabled, but insufficient credits
                      const canTurnOnButInsufficient = !anySwitchOn && !anySwitchOffline && !isSwitchStatusLoading && !isOperatingLights && secondsRemaining > 10 && !hasSufficientCredits;
                      return canTurnOnButInsufficient ? 'text-red-600 font-semibold' : '';
                    })()}>
                      Credits: {formatCurrency(userCredits)}
                    </span>
                    {!hasSufficientCredits && !anySwitchOn && !anySwitchOffline && !isSwitchStatusLoading && !isOperatingLights && secondsRemaining > 10 && (
                      <button
                        onClick={() => setShowLightCreditsModal(true)}
                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        Top Up
                      </button>
                    )}
                  </div>
                </div>
                {/* Second row: Rate on left, Time remaining right-aligned */}
                <div className="flex items-center justify-between text-sm text-blue-800">
                  <span>Rate: {formatCurrency(lightsFeePerMinute)}/min</span>
                  <span>Time remaining: {minutesRemaining} min ({formatCurrency(estimatedCost)})</span>
                </div>
                {lightsStatus.message && (
                  <div
                    className={`text-sm rounded-md border px-3 py-2 ${
                      lightsStatus.type === 'error'
                        ? 'bg-red-100 text-red-700 border-red-200'
                        : 'bg-green-100 text-green-700 border-green-200'
                    }`}
                  >
                    {lightsStatus.message}
                  </div>
                )}
                <div className="bg-white/60 rounded-md p-3 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Switch status</p>
                  {isSwitchStatusLoading ? (
                    <p className="text-xs text-blue-800">Checking switch status...</p>
                  ) : switchStatusError ? (
                    <p className="text-xs text-red-600">{switchStatusError}</p>
                  ) : switchStatuses.length === 0 ? (
                    <p className="text-xs text-blue-800">No switch information available.</p>
                  ) : (
                    <ul className="space-y-1">
                      {switchStatuses.map((status) => {
                        const isError = status.error || !status.exists || !status.online;
                        const isOn = !isError && status.output;
                        const textColor = isError
                          ? 'text-red-600'
                          : isOn
                            ? 'text-green-700'
                            : 'text-blue-800';
                        return (
                          <li key={status.deviceId || status.id} className={`text-xs flex items-center gap-2 ${textColor}`}>
                            <span className="font-semibold">{status.deviceId || 'Switch'}</span>
                            <span>{formatDeviceStatusMessage(status)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleLightsAction('on')}
                      disabled={disableTurnOn}
                      className={`inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-md text-white ${
                        disableTurnOn
                          ? 'bg-blue-300 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                    >
                      {isOperatingLights && lightsAction === 'on' ? 'Turning Lights On…' : 'Turn Lights On'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleLightsAction('off')}
                      disabled={disableTurnOff}
                      className={`inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-md border ${
                        disableTurnOff
                          ? 'border-blue-200 text-blue-300 cursor-not-allowed bg-white'
                          : 'border-blue-400 text-blue-700 bg-white hover:bg-blue-100'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                    >
                      {isOperatingLights && lightsAction === 'off' ? 'Turning Lights Off…' : 'Turn Lights Off'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-blue-800">
                  {anySwitchOn
                    ? `Turn lights off to be credited the remaining time (${minutesRemaining} min): ${formatCurrency(estimatedCost)}`
                    : `Estimated cost for the remaining time (${minutesRemaining} min): ${formatCurrency(estimatedCost)}`}
                </p>
              </div>
            )}

            {grantAccessInlineError ? (
              <div
                role="alert"
                className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                {grantAccessInlineError}
              </div>
            ) : null}

            <div className="flex min-w-0 items-center justify-between gap-2 pt-1 sm:gap-3 border-slate-200">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-1 pr-1 text-xs leading-tight text-blue-600 sm:text-sm sm:leading-normal">
                {showAccessControlAction && parsedClubAccessControl && (
                  <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 sm:gap-3">
                    {parsedClubAccessControl.type === 'access_code' ? (
                      <>
                        <button
                          type="button"
                          disabled={
                            revealedAccessCode !== null ||
                            accessCodeLoading ||
                            Boolean(grantAccessInlineError)
                          }
                          onClick={handleAccessCodeButtonClick}
                          className={`${accessControlPrimaryButtonClass} bg-blue-600 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400 disabled:hover:bg-blue-400`}
                        >
                          {accessCodeLoading
                            ? 'Loading…'
                            : revealedAccessCode !== null
                              ? 'Access Code'
                              : 'Show Access Code'}
                        </button>
                        {revealedAccessCode ? (
                          <span
                            className="min-w-0 select-all break-all font-mono text-lg font-semibold tracking-wide text-green-600 sm:text-xl"
                            aria-live="polite"
                          >
                            {revealedAccessCode}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={disableOpenGateButton}
                        onClick={handleOpenGateClick}
                        className={`${accessControlPrimaryButtonClass} bg-blue-600 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400 disabled:hover:bg-blue-400`}
                      >
                        {openGateLoading ? 'Opening…' : 'Open Gate'}
                      </button>
                    )}
                  </div>
                )}
                {effectiveFee > 0 && userCredits > 0 && (canJoin || isUserPending) && (
                  <span className="tabular-nums">
                    Credits: {formatCurrency(userCredits)}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 whitespace-nowrap"
                >
                  Close
                </button>
                {canEditBooking && canEditBooking.canEdit && !bookingHasEnded && !isWithinCancellationWindow && (
                  <button
                    type="button"
                    onClick={() => onEditBooking(selectedBooking, canEditBooking.editMode)}
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
                  >
                    Edit
                  </button>
                )}
                {canJoin && !bookingHasEnded && (
                  <button
                    type="button"
                    onClick={handleJoinBooking}
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
                  >
                    {effectiveFee > 0 ? `Join R${effectiveFee}` : 'Join'}
                  </button>
                )}
                {isUserPending && !bookingHasEnded && (
                  <button
                    type="button"
                    onClick={handlePayBooking}
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 whitespace-nowrap"
                  >
                    Pay R{effectiveFee}
                  </button>
                )}
                {canLeavePublicBooking && (
                  <button
                    type="button"
                    onClick={handleLeaveBooking}
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 whitespace-nowrap"
                  >
                    Leave
                  </button>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>

      <LightCreditsModal
        isOpen={showLightCreditsModal}
        onClose={() => setShowLightCreditsModal(false)}
        user={user}
        courtData={courtData}
        onSubmit={(amount) => {
          handleCreditPurchaseClick(amount);
        }}
        initialAmount={creditShortfall > 0 ? creditShortfall.toFixed(2) : ''}
      />

      <PaymentModal
        isOpen={showCreditPaymentModal}
        onClose={handleCreditPaymentModalClose}
        totalPaymentAmount={creditPaymentDetails?.totalPaymentAmount || 0}
        reference={creditPaymentDetails?.reference || ''}
        onPaymentComplete={handleCreditPaymentComplete}
        paymentDetails={creditPaymentDetails}
        customText={`
          <ul class="list-disc pl-5">
            <li>Do not close this window until payment completes</li>
            <li>Payment reference: <span class="font-bold">${creditPaymentDetails?.reference || ''}</span></li>
          </ul>
        `}
        showTimer={false}
      />

      <ErrorModal
        isOpen={showCreditErrorModal}
        onClose={() => setShowCreditErrorModal(false)}
        errorMessage={creditErrorMessage}
      />
    </Dialog>
  );
};

export default BookingDetailsModal;